import { i18n } from 'next-i18next';
import { format, intervalToDuration } from 'date-fns';
import { enUS as en, ko } from 'date-fns/locale';

import { t } from '../i18n';

import type { Ago } from './type';

/**
 * 사파리 에러 방지
 * 2022-01-01T12:00:00+0000 포맷이면 +0000을 제거한다.
 */
const transferToISO = (date: string | number) => {
  if (typeof date !== 'string') return date;
  const pattern =
    /([0-9]{4})-([0-1][0-9])-([0-3][0-9])T([01][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])[+]0{4}/;
  return pattern.test(date) ? `${date.slice(0, -5)}Z` : date;
};

/**
 * yyyy.MM.dd 형식으로 날짜 포맷 변환
 */
export const getDate = (date: string) => {
  return format(new Date(transferToISO(date)), 'yyyy.MM.dd');
};

/**
 * yyyy.MM.dd (EEE) HH:mm:ss 형식으로 날짜 포맷 변환
 */
export const getDateTime = (date: string) => {
  const locales = { en, ko };
  const lng = i18n?.language ?? 'ko';

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const locale = locales[lng];
  return format(new Date(transferToISO(date)), 'yyyy.MM.dd (EEE) HH:mm:ss', { locale });
};

/**
 * customFormat에 설정한 형식으로 날짜 포맷 변환
 */
export const getCustomFormatDate = (date: string | number, customFormat: string) => {
  const locales = { en, ko };
  const lng = i18n?.language ?? 'ko';

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const locale = locales[lng];
  return format(new Date(transferToISO(date)), customFormat, { locale });
};

/**
 *
 * withTimestamp true 일 때, getDateTime() 형식으로 날짜 포맷 변환 // `yyyy.MM.dd (EEE) HH:mm:ss`
 *
 * withTimestamp false 일 때, getDate() 형식으로 날짜 포맷 변환 // `yyyy.MM.dd`
 *
 * @param date
 * @param withTimestamp
 * @returns
 */
export const dateStringify = (
  date: string | Date | null,
  withTimestamp?: boolean,
  shouldNotFormatted?: boolean,
) => {
  // date null 일 경우, 빈 문자열 반환
  if (!date) return '';

  /**
   * 브라우저 별로 날짜 인식 포맷이 다른 이슈를 해소하기 위해, new Date 호출 전 . 또는 - 구분자를 / 로 모두 치환한다. (date 데이터가 string 인 경우에만)
   */
  const targetDateString = typeof date === 'string' ? date.replace(/-|\./g, '/') : date.toString();

  // 전달된 date가 유효한 날짜 데이터인지 확인 후, Invalid Date면 빈 문자열 반환
  if (!isValidDate(targetDateString)) return '';

  if (shouldNotFormatted) {
    const notFormattedDateString = new Date(targetDateString).toISOString();

    return notFormattedDateString;
  }

  return withTimestamp ? getDateTime(targetDateString) : getDate(targetDateString);
};

/**
 *
 * withTimestamp true 일 때, getDateTime() 형식으로 날짜 포맷 변환 // `(from:yyyy.MM.dd (EEE) HH:mm:ss~to:yyyy.MM.dd (EEE) HH:mm:ss)`
 *
 * withTimestamp false 일 때, getDate() 형식으로 날짜 포맷 변환 // `(from:yyyy.MM.dd~to:yyyy.MM.dd)`
 * @param dateRange
 * @param withTimestamp
 * @returns
 */
export const dateRangeStringify = (
  dateRange: [string | Date | null, string | Date | null],
  withTimestamp?: boolean,
  shouldNotFormatted?: boolean,
) => {
  const targetDateRangeStringArray = dateRange.map(date =>
    dateStringify(date, withTimestamp, shouldNotFormatted),
  );

  const [fromDateString = '', toDateString = ''] = targetDateRangeStringArray;

  return `(from:${fromDateString}~to:${toDateString})`;
};

/**
 * `(from:yyyy.MM.dd~to:yyyy.MM.dd)` 그리고 `(from:yyyy.MM.dd (EEE) HH:mm:ss~to:yyyy.MM.dd (EEE) HH:mm:ss)` 포맷의 문자열을 [Date|null, Date|null] 포맷으로 변환
 * @param dateRangeString
 * @returns
 */
export const parseAsDateRange = (dateRangeString: string | null) => {
  if (!dateRangeString) return [null, null] as [Date | null, Date | null];

  const removedEscapeString = dateRangeString.replace(/[()]/g, '');

  const [from = '', to = ''] = removedEscapeString.split('~');
  const fromDateString = from.replace('from:', '').replace(/-|\./g, '/');
  const toDateString = to.replace('to:', '').replace(/-|\./g, '/');

  return [
    !fromDateString ? null : new Date(fromDateString),
    !toDateString ? null : new Date(toDateString),
  ] as [Date | null, Date | null];
};

/**
 * true 일 경우, 유효한 Date 객체임을 의미
 * @param date
 * @returns
 */
export const isValidDate = (date: string | Date | null) => {
  if (!date) return false;

  if (date instanceof Date && !isNaN(date.getTime())) return true;

  return !isNaN(new Date(date).getTime());
};

/**
 * 지원되는 날짜 포맷 상수
 */
export const SUPPORTED_DATE_FORMATS = {
  // 기본 날짜 포맷
  DATE_ONLY: 'yyyy.MM.dd',

  // 시간 포함 포맷
  DATE_TIME: 'yyyy.MM.dd HH:mm:ss',
  DATE_TIME_WITHOUT_SECONDS: 'yyyy.MM.dd HH:mm',

  // 요일 포함 포맷
  DATE_WITH_DAY: 'yyyy.MM.dd (EEE)',
  DATE_TIME_WITH_DAY: 'yyyy.MM.dd (EEE) HH:mm:ss',
  DATE_TIME_WITH_DAY_NO_SECONDS: 'yyyy.MM.dd (EEE) HH:mm',

  // 간단한 포맷
  SIMPLE_DATE_TIME: 'yyMMddHHmmss',

  // ISO 포맷
  ISO_DATE: 'yyyy-MM-dd',
  ISO_DATE_TIME: 'yyyy-MM-dd HH:mm:ss',
} as const;

export type SupportedDateFormatKey = keyof typeof SUPPORTED_DATE_FORMATS;

/**
 * 날짜 문자열의 타임존 문제를 해결하기 위한 함수
 *
 * date 관련 값이 여러가지 형태로 내려오고 있고, 1번 케이스에만 timezone 문제가 있기 때문에 해당 케이스에 대한 추가로직을 넣어준다.
 * 1) YYYY-MM-DDTHH:mm:ss
 * 2) YYYY-MM-DDTHH:mm:ss+00:00
 * 3) YYYY-MM-DDTHH:mm:ssZ
 *
 * @param date - 변환할 날짜 문자열
 * @returns 타임존이 적용된 날짜 문자열
 */
export const convertDateFormat = (date: string) => {
  const pattern =
    /([0-9]{4})-([0-1][0-9])-([0-3][0-9])T([01][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])$/;

  return pattern.test(date) ? `${date}Z` : date;
};

/**
 * 현재 시간과 주어진 시간의 차이를 계산하여 반환
 *
 * @param value - 비교할 날짜 문자열
 * @returns 시간 차이 객체 (년, 월, 일, 시, 분, 초)
 */
export const getAgo = (value: string): Ago => {
  const convertedValue = convertDateFormat(value);
  const { years, months, days, hours, minutes, seconds } = intervalToDuration({
    start: convertedValue ? new Date(convertedValue) : new Date(),
    end: new Date(),
  });

  return {
    years: String(years),
    months: String(months),
    days: String(days),
    hours: String(hours),
    minutes: String(minutes),
    seconds: String(seconds),
  };
};

/**
 * 지정된 포맷으로 날짜 문자열을 반환
 *
 * @param value - 포맷팅할 날짜 문자열
 * @param formatKey - 사용할 날짜 포맷 키
 * @returns 포맷팅된 날짜 문자열
 */
export const getDateStringWithFormat = (
  value: string,
  formatKey: SupportedDateFormatKey,
): string => {
  const formatStr = SUPPORTED_DATE_FORMATS[formatKey] || 'yyyy-MM-dd';
  const dateStr = !value ? new Date().toString() : value;

  return getCustomFormatDate(dateStr, formatStr);
};

/**
 * 날짜를 요일이 포함된 형식으로 반환 (yyyy.MM.dd (EEE) HH:mm:ss)
 *
 * @param value - 포맷팅할 날짜 문자열 (기본값: 빈 문자열)
 * @returns 요일이 포함된 날짜 문자열
 */
export const getDateStringWithDay = (value = ''): string => {
  return getDateStringWithFormat(value, 'DATE_TIME_WITH_DAY');
};

/**
 * Ago 객체를 사용하여 사람이 읽기 쉬운 기간 문자열을 생성
 *
 * 값이 0이 아닌 단위만 넓은 단위 순서로 취한 뒤, `maxNonZeroUnits`개까지 조합하여 반환
 * (중간에 0인 단위는 건너뜀. 예: 8일, 0시, 8분 → "8일, 8분" — 기본 2개)
 * 모두 0이면 가장 작은 단위(분) 하나만 반환
 *
 * @param ago - 시간 차이 객체
 * @param maxNonZeroUnits - 표시할 0이 아닌 단위의 최대 개수 (기본: 2, 1~5로 클램핑)
 * @param joinSeparator - 단위 사이 구분자 (기본: `, `)
 * @returns 기간을 나타내는 문자열
 */
export const getDuration = (ago: Ago, maxNonZeroUnits = 2, joinSeparator = ', '): string => {
  if (!ago) return '';

  const { years, months, days, hours, minutes } = ago;

  const items = [
    { value: Number(years), label: t('common:common.duration.unit.year', { value: years }) },
    { value: Number(months), label: t('common:common.duration.unit.month', { value: months }) },
    { value: Number(days), label: t('common:common.duration.unit.day', { value: days }) },
    { value: Number(hours), label: t('common:common.duration.unit.hours', { value: hours }) },
    { value: Number(minutes), label: t('common:common.duration.unit.minute', { value: minutes }) },
  ];

  const limit = Math.max(1, Math.min(maxNonZeroUnits, items.length));
  const nonZero = items.filter(item => item.value !== 0);
  const fallback = items.at(-1);
  const durationArray =
    nonZero.length > 0 ? nonZero.slice(0, limit) : fallback != null ? [fallback] : [];
  return durationArray.map(duration => duration.label).join(joinSeparator);
};

/**
 * 일시와 현재까지의 경과 시간을 함께 표시하는 문자열을 생성
 *
 * 상세 화면의 생성 일시 표기 규칙(`yyyy.MM.dd (EEE) HH:mm:ss (N시간, N분)`)으로,
 * `getDateTime` 과 `getDuration(getAgo())` 를 조합한 결과와 같다.
 * 경과 시간 단위는 `getDuration` 기본값(0이 아닌 상위 2개 단위)을 따른다.
 *
 * @param date - 포맷팅할 일시 (ISO 문자열, epoch(ms), Date)
 * @returns 일시와 경과 시간이 병기된 문자열
 *
 * @example
 * ```typescript
 * formatDateTimeWithDuration('2025-03-20T12:25:37Z') // "2025.03.20 (목) 21:25:37 (16시간, 10분)"
 * formatDateTimeWithDuration(1742473537000)          // 같은 결과
 * ```
 */
export const formatDateTimeWithDuration = (date: string | number | Date): string => {
  const isoDate = new Date(date).toISOString();

  return `${getDateTime(isoDate)} (${getDuration(getAgo(isoDate))})`;
};
